import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Marker, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  X, Plane, PlaneTakeoff, PlaneLanding, MapPin, Package, PackageCheck,
  Weight, Clock, ExternalLink, FileText, Circle, CheckCircle2, ShieldCheck,
  ClipboardCheck, Truck, Building2, Maximize2, Minimize2, Copy, Check, Bell,
} from 'lucide-react';
import airportCoordinates from '../../config/airportCoordinates.json';

// Free, key-less vector basemap (CARTO) — no Mapbox token required.
// Deliberately kept light even in dark mode: the map reads better as a bright
// card against the rest of the dark UI than a low-contrast dark-on-dark map.
const MAP_STYLE_LIGHT = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

const TRACKING_API_BASE = 'https://track.quickglobal.in/api/v1/shipments';
const TRACKING_API_KEY = 'f884559c5ca7d43529df08550ffec1de79cc93c768d2d035';
const FETCH_TIMEOUT_MS = 15000;
const POLL_INTERVAL_MS = 4000;

// Different carriers report tracking "location" in different shapes — some use the
// 3-letter IATA code ("HKG"), others spell out the city ("HONG KONG", "NEW DELHI").
// Build a city-name → IATA-code index once so either form resolves to the same airport.
const CITY_TO_CODE = Object.fromEntries(
  Object.entries(airportCoordinates).map(([code, info]) => [info.city.toLowerCase(), code])
);
// Older/colonial city names some carriers still use, that don't match our "city" field.
const LOCATION_ALIASES = {
  'new delhi': 'DEL',
  'bombay': 'BOM',
  'calcutta': 'CCU',
  'madras': 'MAA',
};
const resolveAirportCode = (raw) => {
  if (!raw) return null;
  const clean = String(raw).trim();
  if (!clean) return null;
  const upper = clean.toUpperCase();
  if (airportCoordinates[upper]) return upper;
  const lower = clean.toLowerCase();
  return LOCATION_ALIASES[lower] || CITY_TO_CODE[lower] || null;
};

// ── Status → icon + color treatment (broad keyword match) ──────────────────
const STATUS_STYLES = [
  { test: /deliver/i,        icon: PackageCheck,   ring: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  { test: /custom/i,         icon: ShieldCheck,     ring: 'bg-amber-500',   badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  { test: /depart/i,         icon: PlaneTakeoff,    ring: 'bg-violet-500',  badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300' },
  { test: /arriv/i,          icon: PlaneLanding,    ring: 'bg-cyan-500',    badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300' },
  { test: /notif/i,          icon: Bell,            ring: 'bg-indigo-500',  badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300' },
  { test: /transit|uplift/i, icon: Plane,           ring: 'bg-sky-500',     badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  { test: /freight on hand|accept/i, icon: Truck,   ring: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  { test: /received from flight/i,   icon: PlaneLanding, ring: 'bg-slate-400', badge: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300' },
  { test: /manifest for delivery/i,  icon: FileText, ring: 'bg-slate-400', badge: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300' },
  { test: /manifest|book/i,  icon: Package,          ring: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  { test: /clearance/i,      icon: ClipboardCheck,   ring: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
];
const DEFAULT_STATUS_STYLE = { icon: Circle, ring: 'bg-slate-400', badge: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' };

const statusStyleFor = (status) => STATUS_STYLES.find(s => s.test.test(status || ''))
  ? { ...STATUS_STYLES.find(s => s.test.test(status || '')) }
  : DEFAULT_STATUS_STYLE;

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const formatShortDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};

const formatDuration = (startIso, endIso) => {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!(ms > 0)) return '—';
  const totalMin = Math.round(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  return `${d > 0 ? `${d}d ` : ''}${h}h ${m}m`;
};

// ── Great-circle helpers — both for the drawn arc and the km distance ──────
const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.asin(Math.sqrt(a)));
};
const geodesicArc = (lat1, lng1, lat2, lng2, steps = 48) => {
  const φ1 = toRad(lat1), λ1 = toRad(lng1), φ2 = toRad(lat2), λ2 = toRad(lng2);
  const d = 2 * Math.asin(Math.sqrt(Math.sin((φ2 - φ1) / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin((λ2 - λ1) / 2) ** 2));
  if (d === 0) return [[lng1, lat1]];
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const f = i / steps;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
    const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
    const z = A * Math.sin(φ1) + B * Math.sin(φ2);
    pts.push([toDeg(Math.atan2(y, x)), toDeg(Math.atan2(z, Math.sqrt(x * x + y * y)))]);
  }
  return pts;
};

// ── Branded loader — a plane flying along the shipment's own route line ────
const TrackingLoader = ({ awbNumber }) => (
  <div className="flex flex-col items-center justify-center py-24 px-6">
    <svg width="220" height="70" viewBox="0 0 220 70" className="mb-6" aria-hidden="true">
      <path d="M10,55 C 60,15 120,85 210,20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 6" className="text-blue-200 dark:text-blue-900" />
      <circle cx="10" cy="55" r="4" className="fill-blue-600 dark:fill-blue-400" />
      <circle cx="210" cy="20" r="4" className="fill-slate-300 dark:fill-slate-600" />
      <g style={{ offsetPath: "path('M10,55 C 60,15 120,85 210,20')", animation: 'awb-fly 1.8s linear infinite' }}>
        <Plane size={18} className="text-blue-600 dark:text-blue-400" style={{ transform: 'rotate(35deg)' }} />
      </g>
      <style>{`
        @keyframes awb-fly { to { offset-distance: 100%; } }
        @media (prefers-reduced-motion: reduce) {
          g[style*="offset-path"] { animation: none !important; offset-distance: 55%; }
        }
      `}</style>
    </svg>
    <p className="font-semibold text-slate-800 dark:text-slate-100">Fetching live tracking…</p>
    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">AWB {awbNumber} — this can take a few seconds</p>
  </div>
);

// ── Real, minimal map: no clutter of country/city labels, just the route ───
const RouteMap = ({ waypoints, expanded, onToggleExpand }) => {
  const mapRef = useRef(null);

  const routeCoords = useMemo(() => {
    const coords = [];
    for (let i = 0; i < waypoints.length - 1; i++) {
      const seg = geodesicArc(waypoints[i].lat, waypoints[i].lng, waypoints[i + 1].lat, waypoints[i + 1].lng);
      coords.push(...(i === 0 ? seg : seg.slice(1)));
    }
    return coords;
  }, [waypoints]);

  const routeGeoJson = useMemo(
    () => (routeCoords.length > 1 ? { type: 'Feature', geometry: { type: 'LineString', coordinates: routeCoords } } : null),
    [routeCoords]
  );

  const midpoint = routeCoords[Math.floor(routeCoords.length / 2)];
  const bearing = useMemo(() => {
    if (routeCoords.length < 2) return 0;
    const mid = Math.floor(routeCoords.length / 2);
    const [lng1, lat1] = routeCoords[Math.max(0, mid - 1)];
    const [lng2, lat2] = routeCoords[Math.min(routeCoords.length - 1, mid + 1)];
    return (toDeg(Math.atan2(lng2 - lng1, lat2 - lat1)) + 360) % 360;
  }, [routeCoords]);

  const bounds = useMemo(() => {
    const lngs = waypoints.map(w => w.lng);
    const lats = waypoints.map(w => w.lat);
    return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
  }, [waypoints]);

  // Generous, roomy padding on every side — the label pills float *above* each
  // marker (anchor="bottom"), so the top especially needs real headroom or it clips.
  const fitPadding = { top: 80, bottom: 70, left: 60, right: 60 };

  const fit = (duration) => {
    const map = mapRef.current;
    if (!map) return;
    map.resize();
    // Run after the browser has actually painted the current container size —
    // fitting against a mid-layout/mid-transition size is what pushes the
    // route off-center and clips a marker at one edge.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try { map.fitBounds(bounds, { padding: fitPadding, maxZoom: 4.5, duration }); } catch { /* not ready yet */ }
      });
    });
  };

  useEffect(() => { fit(400); }, [bounds, expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // GL canvases don't auto-adjust when their container is resized by CSS/React state
  // (e.g. the expand/collapse toggle, or the grid finishing its layout pass) — without
  // this the map keeps rendering at its old size and looks cropped. Watch the actual
  // container box and force a resize + re-fit whenever it changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const container = map.getContainer?.();
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => fit(0));
    ro.observe(container);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds]);

  // Keep country-level labels for geographic context; hide the denser stuff
  // (cities, roads, POIs, house numbers) that makes the basemap feel cluttered.
  const handleLoad = (evt) => {
    const map = evt.target;
    try {
      map.getStyle().layers.forEach((layer) => {
        if (layer.type !== 'symbol') return;
        if (layer.id.toLowerCase().includes('country')) return;
        map.setLayoutProperty(layer.id, 'visibility', 'none');
      });
    } catch { /* style not ready */ }
    fit(0);
  };

  return (
    <div className="relative w-full h-full">
      <Map
        ref={mapRef}
        mapStyle={MAP_STYLE_LIGHT}
        initialViewState={{ longitude: waypoints[0].lng, latitude: waypoints[0].lat, zoom: 2 }}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
        interactive={false}
        onLoad={handleLoad}
      >
        {routeGeoJson && (
          <Source id="route" type="geojson" data={routeGeoJson}>
            <Layer id="route-glow" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': '#3b82f6', 'line-width': 6, 'line-opacity': 0.18, 'line-blur': 2 }} />
            <Layer id="route-line" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': '#2563eb', 'line-width': 2.5, 'line-dasharray': [0.1, 1.4] }} />
          </Source>
        )}

        {waypoints.map((w, i) => {
          const isOrigin = i === 0;
          const isLatest = i === waypoints.length - 1;
          return (
            <Marker key={w.code} longitude={w.lng} latitude={w.lat} anchor="bottom">
              <div className="flex flex-col items-center gap-1">
                <span className={`flex items-center gap-1 text-[10px] font-bold pl-1 pr-2 py-0.5 rounded shadow-sm whitespace-nowrap ${isLatest ? 'bg-emerald-500 text-white' : isOrigin ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>
                  <MapPin size={10} className={isLatest || isOrigin ? 'text-white/80' : 'text-slate-400'} />
                  <span>
                    {w.code}
                    {w.city && <span className="font-medium opacity-80"> · {w.city}</span>}
                  </span>
                </span>
                <span className="relative flex items-center justify-center">
                  {isLatest && <span className="absolute inline-flex h-5 w-5 rounded-full bg-emerald-400 opacity-50 animate-ping" />}
                  <span className={`relative flex items-center justify-center rounded-full border-2 border-white dark:border-slate-900 shadow ${isLatest ? 'w-4 h-4 bg-emerald-500' : isOrigin ? 'w-4 h-4 bg-blue-600' : 'w-2.5 h-2.5 bg-blue-400'}`} />
                </span>
              </div>
            </Marker>
          );
        })}

        {midpoint && (
          <Marker longitude={midpoint[0]} latitude={midpoint[1]} anchor="center">
            <span className="relative flex items-center justify-center">
              <span className="absolute inline-flex h-9 w-9 rounded-full bg-blue-400 opacity-40 animate-ping" />
              <span className="relative w-7 h-7 rounded-full bg-blue-600 border-2 border-white shadow-lg flex items-center justify-center">
                {/* lucide's Plane glyph points ~45° off north by default — correct for that so it visually faces the direction of travel */}
                <Plane size={13} className="text-white" style={{ transform: `rotate(${bearing - 45}deg)` }} />
              </span>
            </span>
          </Marker>
        )}
      </Map>

      <button
        type="button"
        onClick={onToggleExpand}
        className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur text-[11px] font-semibold text-slate-700 dark:text-slate-200 shadow hover:bg-white dark:hover:bg-slate-800 transition"
      >
        {expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
        {expanded ? 'Collapse Map' : 'View Full Map'}
      </button>
    </div>
  );
};

// ── Flight progress strip: origin —— plane —— destination, with km/duration ──
const FlightProgress = ({ origin, destination, distanceKm, duration, departedAt, arrivedAt, arrivedLabel }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
    <div className="flex items-center gap-3">
      <div className="flex flex-col items-center flex-shrink-0 w-20">
        <span className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
          <PlaneTakeoff size={16} className="text-blue-600 dark:text-blue-400" />
        </span>
        <span className="text-sm font-bold text-slate-900 dark:text-white mt-1.5">{origin.code}</span>
        <span className="text-[11px] text-slate-400 dark:text-slate-500 text-center leading-tight">{origin.city || ''}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="relative h-6 flex items-center">
          <div className="absolute left-0 right-0 h-0.5 border-t-2 border-dashed border-blue-200 dark:border-blue-900" />
          <span className="relative mx-auto flex items-center justify-center">
            <span className="absolute inline-flex h-9 w-9 rounded-full bg-blue-400 opacity-30 animate-ping" />
            <span className="relative w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center shadow">
              {/* Flow runs left→right here, so point the (~45°-off-north by default) glyph due east */}
              <Plane size={13} className="text-white" style={{ transform: 'rotate(45deg)' }} />
            </span>
          </span>
        </div>
        <div className="text-center mt-1">
          {distanceKm != null && <p className="text-xs font-bold text-slate-700 dark:text-slate-200">{distanceKm.toLocaleString('en-IN')} km</p>}
          <p className="text-[11px] text-slate-400 dark:text-slate-500">{duration}</p>
        </div>
      </div>

      <div className="flex flex-col items-center flex-shrink-0 w-20">
        <span className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
          <MapPin size={16} className="text-emerald-600 dark:text-emerald-400" />
        </span>
        <span className="text-sm font-bold text-slate-900 dark:text-white mt-1.5">{destination.code}</span>
        <span className="text-[11px] text-slate-400 dark:text-slate-500 text-center leading-tight">{destination.city || ''}</span>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <PlaneTakeoff size={14} className="text-slate-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Departed</p>
          <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{origin.code}{origin.city ? ` (${origin.city})` : ''}</p>
          <p className="text-[10px] text-slate-400 dark:text-slate-500">{formatShortDateTime(departedAt)}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Clock size={14} className="text-slate-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Duration</p>
          <p className="text-xs font-bold text-slate-800 dark:text-slate-100">{duration}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <CheckCircle2 size={14} className="text-slate-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{arrivedLabel}</p>
          <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{destination.code}{destination.city ? ` (${destination.city})` : ''}</p>
          <p className="text-[10px] text-slate-400 dark:text-slate-500">{formatShortDateTime(arrivedAt)}</p>
        </div>
      </div>
    </div>
  </div>
);

// ── Timeline — icon-per-step stepper, connected by a running line ──────────
// Newest first — the current/latest status reads at a glance at the top.
const Timeline = ({ history }) => {
  const newestFirst = [...history].reverse();
  return (
    <div>
      {newestFirst.map((h, i) => {
        const s = statusStyleFor(h.status);
        const Icon = s.icon;
        const isLatest = i === 0;
        const isOldest = i === newestFirst.length - 1;
        // Carriers sometimes ping the same status repeatedly (e.g. "ACCEPTED" logged
        // 4 times over a few minutes) — those get collapsed into one timeline step with
        // the individual pings kept in `occurrences`, newest last.
        const occurrences = h.occurrences || [];
        return (
          <div key={i} className="flex gap-3.5">
            <div className="flex flex-col items-center flex-shrink-0">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center ring-4 ${isLatest ? 'ring-emerald-100 dark:ring-emerald-900/30' : 'ring-white dark:ring-slate-800'} ${s.ring}`}>
                <Icon size={15} className="text-white" strokeWidth={2.25} />
              </span>
              {!isOldest && <span className="w-0.5 flex-1 min-h-[26px] bg-slate-200 dark:bg-slate-700 my-1" />}
            </div>

            <div className={`flex-1 min-w-0 rounded-lg px-3 py-2.5 mb-3 ${isLatest ? 'bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/60' : ''}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${s.badge}`}>{h.status}</span>
                <span className="flex items-center gap-1 text-xs font-semibold text-slate-700 dark:text-slate-300">
                  <MapPin size={11} className="text-slate-400" />{h.location}
                </span>
                <span className="text-[11px] text-slate-400 dark:text-slate-500 sm:ml-auto whitespace-nowrap w-full sm:w-auto">{formatTimestamp(h.timestamp)}</span>
              </div>
              {h.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-snug">{h.description}</p>}

              {occurrences.length > 1 && (
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700 space-y-1">
                  {occurrences.map((occ, oi) => (
                    <div key={oi} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px] text-slate-400 dark:text-slate-500 leading-snug">
                      <span className="flex-1 min-w-0">{occ.carrierStatus}</span>
                      <span className="flex-shrink-0 whitespace-nowrap">{formatTimestamp(occ.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AwbTrackingModal = ({ awbNumber, onClose, onFallback }) => {
  const [phase, setPhase] = useState('loading'); // 'loading' | 'success' | 'error'
  const [data, setData] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const fellBack = useRef(false);

  // The AWB is the one thing people lift off this screen to paste elsewhere.
  const copyAwb = () => {
    navigator.clipboard?.writeText(String(awbNumber)).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }).catch(() => { /* clipboard blocked — the number is selectable anyway */ });
  };

  useEffect(() => {
    let cancelled = false;
    let pollTimeoutId = null;

    const runFallback = () => {
      if (cancelled || fellBack.current) return;
      fellBack.current = true;
      onFallback();
      onClose();
    };

    const poll = async () => {
      const controller = new AbortController();
      const abortTimeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(`${TRACKING_API_BASE}/${encodeURIComponent(awbNumber)}`, {
          signal: controller.signal,
          headers: { 'X-API-Key': TRACKING_API_KEY },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        if (!json || !json.status) throw new Error('Malformed tracking response');

        // Carrier data isn't ready yet — keep the loader up and check again shortly.
        if (json.status === 'processing') {
          setPhase('loading');
          pollTimeoutId = setTimeout(poll, POLL_INTERVAL_MS);
          return;
        }

        setData(json);
        setPhase('success');
      } catch (err) {
        // A cancelled request (component unmounted, or React StrictMode's dev-only
        // double-mount) isn't a real failure — nothing to warn about or fall back on.
        if (cancelled) return;
        console.warn('AWB live tracking unavailable, falling back to carrier site:', err.message);
        setPhase('error');
        runFallback();
      } finally {
        clearTimeout(abortTimeoutId);
      }
    };

    poll();

    // Deliberately not aborting the in-flight request from inside this cleanup: aborting
    // from a React effect cleanup is what raises the (harmless but noisy) "signal is
    // aborted without reason" console error under StrictMode's dev-only double-invoke.
    // The `cancelled` flag above is enough to ignore a late response — the in-flight
    // request is left to finish or hit its own timeout-triggered abort instead.
    return () => { cancelled = true; clearTimeout(pollTimeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awbNumber]);

  const sortedHistory = useMemo(() => {
    return [...(data?.trackingHistory || [])]
      .filter((h) => h.status !== 'UNKNOWN')
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [data]);

  const destinationCode = data?.destination || sortedHistory[sortedHistory.length - 1]?.location || null;
  const isDelivered = /deliver/i.test(data?.status || '');

  const waypoints = useMemo(() => {
    if (!data) return [];
    const seen = new Set();
    const result = [];
    const tryAdd = (raw) => {
      const code = resolveAirportCode(raw);
      if (code && !seen.has(code)) { seen.add(code); result.push({ code, ...airportCoordinates[code] }); }
    };
    tryAdd(data.origin || sortedHistory[0]?.location);
    sortedHistory.forEach(h => tryAdd(h.location));
    tryAdd(destinationCode);
    return result;
  }, [data, sortedHistory, destinationCode]);

  const distanceKm = waypoints.length >= 2
    ? haversineKm(waypoints[0].lat, waypoints[0].lng, waypoints[waypoints.length - 1].lat, waypoints[waypoints.length - 1].lng)
    : null;

  const departedAt = sortedHistory[0]?.timestamp || null;
  const arrivedAt = sortedHistory[sortedHistory.length - 1]?.timestamp || null;
  const duration = formatDuration(departedAt, arrivedAt);

  const StatusIcon = isDelivered ? CheckCircle2 : Plane;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col rounded-3xl shadow-2xl
                   bg-white dark:bg-[#0c1526] border border-transparent dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — the banner is 3:1 with white bands above and below the
            picture, so the box is given a height of its own and the image
            cropped to the middle of it rather than fitted to the padding. */}
        <div
          className="relative flex-shrink-0 flex items-center px-5 sm:px-6 h-[116px] sm:h-[132px]
                     bg-no-repeat bg-cover"
          style={{
            backgroundImage: "url('/images/theme-assets/tracking-header.png')",
            backgroundPosition: 'center 54%',
          }}
        >
          {/* The picture is bright right across, and white at its left edge.
              One flat veil knocks it back to the panel, and the wash over it
              clears a readable field for the title. */}
          <span aria-hidden="true" className="absolute inset-0 bg-white/35 dark:bg-[#0c1526]/70" />
          <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-r
            from-white via-white/80 to-transparent
            dark:from-[#0c1526] dark:via-[#0c1526]/80 dark:to-transparent" />

          <div className="relative flex-1 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="grid place-items-center w-12 h-12 rounded-2xl flex-shrink-0
                               bg-gradient-to-br from-blue-500 to-blue-600 text-white
                               shadow-lg shadow-blue-600/30">
                <Plane size={22} />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-[21px] font-black tracking-tight text-slate-900 dark:text-white leading-tight">
                    Shipment Tracking
                  </h2>
                  {phase === 'success' && data && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold
                      ${isDelivered
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30'
                        : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30'}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {isDelivered ? 'Delivered' : data.status}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={copyAwb}
                  title={copied ? 'Copied' : 'Copy AWB number'}
                  className="group mt-0.5 inline-flex items-center gap-1.5 text-[13px] font-mono font-semibold
                             text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100 transition"
                >
                  #{awbNumber}
                  {copied
                    ? <Check size={13} className="text-emerald-500" />
                    : <Copy size={13} className="opacity-60 group-hover:opacity-100" />}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid place-items-center w-10 h-10 rounded-full flex-shrink-0 transition
                         bg-white/90 text-slate-600 shadow-md hover:bg-white hover:text-slate-900
                         dark:bg-slate-800/90 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          {phase === 'loading' && <TrackingLoader awbNumber={awbNumber} />}

          {phase === 'success' && data && (
            <div className="grid md:grid-cols-[1.15fr_1fr]">
              {/* ── LEFT: summary, map, flight strip, documents ── */}
              <div className="p-6 space-y-5 md:border-r border-slate-100 dark:border-slate-700">
                {/* Status banner */}
                <div className={`relative overflow-hidden rounded-xl p-4 ${isDelivered ? 'bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/60' : 'bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800/60'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isDelivered ? 'bg-emerald-500' : 'bg-blue-600'}`}>
                      <StatusIcon size={17} className="text-white" />
                    </span>
                    <div>
                      <p className={`font-bold text-sm ${isDelivered ? 'text-emerald-800 dark:text-emerald-300' : 'text-blue-800 dark:text-blue-300'}`}>
                        {isDelivered ? 'Your shipment has been delivered' : data.status}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {isDelivered ? `Delivered on ${formatShortDateTime(arrivedAt)}` : `Last update: ${formatShortDateTime(data.retrievedAt)}`}
                      </p>
                    </div>
                  </div>
                  <Package size={72} strokeWidth={1} className={`absolute -right-3 -bottom-3 opacity-10 ${isDelivered ? 'text-emerald-600' : 'text-blue-600'}`} />
                </div>

                <p className="text-xs text-slate-400 dark:text-slate-500 -mt-2">
                  Source: {data.source || data.carrier?.name || '—'} · Updated {formatTimestamp(data.retrievedAt)}
                </p>

                {/* Stat tiles */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: Building2, box: 'bg-blue-100 dark:bg-blue-900/40', ic: 'text-blue-600 dark:text-blue-400', label: 'Carrier', value: data.carrier?.name || '—' },
                    { icon: Package, box: 'bg-violet-100 dark:bg-violet-900/40', ic: 'text-violet-600 dark:text-violet-400', label: 'Pieces', value: data.pieces || '—' },
                    { icon: Weight, box: 'bg-amber-100 dark:bg-amber-900/40', ic: 'text-amber-600 dark:text-amber-400', label: 'Weight', value: data.weight || '—' },
                  ].map((t) => (
                    <div key={t.label} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-2 ${t.box}`}>
                        <t.icon size={14} className={t.ic} />
                      </div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{t.label}</div>
                      <div className="text-sm font-bold text-slate-900 dark:text-white leading-snug" title={t.value}>{t.value}</div>
                    </div>
                  ))}
                </div>

                {/* Map */}
                {waypoints.length >= 2 && (
                  <div
                    className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 transition-all"
                    style={{ height: mapExpanded ? 460 : 300 }}
                  >
                    <RouteMap waypoints={waypoints} expanded={mapExpanded} onToggleExpand={() => setMapExpanded(v => !v)} />
                  </div>
                )}

                {/* Flight progress */}
                {waypoints.length >= 2 && (
                  <FlightProgress
                    origin={waypoints[0]}
                    destination={waypoints[waypoints.length - 1]}
                    distanceKm={distanceKm}
                    duration={duration}
                    departedAt={departedAt}
                    arrivedAt={arrivedAt}
                    arrivedLabel={isDelivered ? 'Delivered' : 'Last Seen'}
                  />
                )}
              </div>

              {/* ── RIGHT: tracking history ── */}
              <div className="p-5 sm:p-6 bg-slate-50/70 dark:bg-slate-900/40">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="flex items-center gap-2 text-[15px] font-bold text-slate-900 dark:text-white">
                    <span className="grid place-items-center w-7 h-7 rounded-lg
                                     bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                      <Clock size={15} />
                    </span>
                    Tracking History
                  </h3>
                  <button
                    type="button"
                    onClick={() => { onFallback(); }}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[12.5px] font-bold transition
                               border border-slate-200 bg-white text-blue-600 hover:bg-blue-50
                               dark:border-slate-700 dark:bg-slate-800/70 dark:text-blue-300 dark:hover:bg-slate-700/70"
                  >
                    View on {data.carrier?.name || 'carrier'} <ExternalLink size={13} />
                  </button>
                </div>
                <Timeline history={sortedHistory} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AwbTrackingModal;
